// src/components/ProtectedRoute/Admin/Dialogs/DialogTaxonomicActUpsert.jsx
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Autocomplete,
  Stack,
  RadioGroup,
  FormControlLabel,
  Radio,
  Typography,
  InputAdornment,
  Tooltip,
  Popper,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import ScienceIcon from "@mui/icons-material/Science";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import CategoryIcon from "@mui/icons-material/Category";
import LinkIcon from "@mui/icons-material/Link";
import NotesIcon from "@mui/icons-material/Notes";
import PageviewIcon from "@mui/icons-material/Pageview";
import RuleIcon from "@mui/icons-material/Rule";
import LooksOneIcon from "@mui/icons-material/LooksOne";
import GroupWorkIcon from "@mui/icons-material/GroupWork";
import FindReplaceIcon from "@mui/icons-material/FindReplace";
import BiotechIcon from "@mui/icons-material/Biotech";
import { useEffect, useMemo, useState, useCallback } from "react";
import { v4 as uuidv4 } from "uuid";
import supabase from "../../../../utils/supabase";
import fetchSupabaseAll from "../../../../utils/fetchSupabaseAll";
import LoadingScreen from "../../../LoadingScreen";
import DialogPublicationUpsert from "./DialogPublicationUpsert";
import DialogScientificNameEditor from "./DialogScientificNameEditor";
import DialogSpecimen from "./DialogSpecimen";
import AuditLogUpdater from "../../AuditLogUpdater/AuditLogUpdater";
import { useStatusDialog } from "../../../common/StatusDialogProvider";

const AutocompletePopper = (props) => (
  <Popper {...props} style={{ width: "fit-content", minWidth: "300px" }} placement="bottom-start" />
);

const buildDefaultForm = () => ({
  id: uuidv4(),
  scientific_name_id: "",
  publication_id: "",
  act_type_id: "",
  related_name_id: "",
  replacement_name_id: null,
  rank_change_to: "",
  type_specimen_id: "",
  type_taxon_id: "",
  page: "",
  validity: "true",
  remarks: "",
});

const normalizePublicationOption = (p) => {
  if (!p) return "";
  try {
    const authors = (p.publications_authors || [])
      .sort((a, b) => (a?.author_order ?? 0) - (b?.author_order ?? 0))
      .map((pa) => pa?.authors?.last_name_eng || "")
      .filter(Boolean);
    let authorStr = authors.join(", ");
    if (authors.length > 3) authorStr = authors.slice(0, 3).join(", ") + ", et al.";
    const year = p.publication_date ? new Date(p.publication_date).getFullYear() : "n.d.";
    const journalInfo = p.journal?.name_english ? `  E${p.journal.name_english}` : "";
    const vol = p.volume ? ` ${p.volume}` : "";
    const num = p.number ? `(${p.number})` : "";
    const pageInfo = p.page ? `: ${p.page}` : "";
    const title = p.title_english || "[No Title]";
    return `${authorStr} (${year}) ${title}${journalInfo}${vol}${num}${pageInfo} [ID:${p.id}]`;
  } catch (e) {
    return `Publication ${p?.id || ""}`;
  }
};

export default function DialogTaxonomicActUpsert({ open = true, mode = "add", taxonomicAct = null, onClose }) {
  const isEdit = mode === "edit";
  const { showStatus } = useStatusDialog();

  const [form, setForm] = useState(buildDefaultForm());
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [scientificNames, setScientificNames] = useState([]);
  const [publications, setPublications] = useState([]);
  const [actTypes, setActTypes] = useState([]);
  const [ranks, setRanks] = useState([]);
  const [specimens, setSpecimens] = useState([]);
  const [specimensAvailable, setSpecimensAvailable] = useState(false);
  const [showAddPublication, setShowAddPublication] = useState(false);
  const [showAddScientificName, setShowAddScientificName] = useState(false);
  const [showAddSpecimen, setShowAddSpecimen] = useState(false);
  const [auditLogProps, setAuditLogProps] = useState(null);

  // ---------- init / data fetch ----------
  const assignForm = useCallback(
    (act) => {
      if (act) {
        setForm({
          id: act.id || "",
          scientific_name_id: act.scientific_name_id || "",
          publication_id: act.publication_id || "",
          act_type_id: act.act_type_id || "",
          related_name_id: act.related_name_id || "",
          replacement_name_id: act.replacement_name_id || null,
          rank_change_to: act.rank_change_to || "",
          type_specimen_id: act.type_specimen_id || "",
          type_taxon_id: act.type_taxon_id || "",
          page: act.page || "",
          validity: act.validity === false ? "false" : "true",
          remarks: act.remarks || "",
        });
      } else {
        setForm(buildDefaultForm());
      }
    },
    []
  );

  const fetchOptions = useCallback(async () => {
    setLoading(true);
    setFetchError(null);
    setSpecimensAvailable(false);
    setSpecimens([]);

    try {
      const sciData = await fetchSupabaseAll("scientific_names", "id, name_spell_valid, current_rank");
      if (sciData) setScientificNames(sciData || []);

      const results = await Promise.allSettled([
        supabase.from("publications").select(`
          id, title_english, publication_date, volume, number, page,
          journal:journal_id(name_english),
          publications_authors(author_order, authors(last_name_eng))
        `),
        supabase.from("taxonomic_act_types").select("id, name, code"),
        supabase.from("rank").select("id"),
        supabase.from("specimens").select("id, specimen_code"),
      ]);

      const [pubResult, actResult, rkResult, spmResult] = results;
      let firstError = null;

      if (pubResult.status === "fulfilled" && !pubResult.value.error) {
        setPublications(pubResult.value.data || []);
      } else {
        firstError =
          firstError ||
          pubResult.reason?.message ||
          pubResult.value?.error?.message ||
          "Failed to fetch publications.";
        setPublications([]);
      }

      if (actResult.status === "fulfilled" && !actResult.value.error) {
        setActTypes(actResult.value.data || []);
      } else {
        firstError =
          firstError ||
          actResult.reason?.message ||
          actResult.value?.error?.message ||
          "Failed to fetch act types.";
        setActTypes([]);
      }

      if (rkResult.status === "fulfilled" && !rkResult.value.error) {
        setRanks(rkResult.value.data?.map((r) => r.id) || []);
      } else {
        firstError =
          firstError ||
          rkResult.reason?.message ||
          rkResult.value?.error?.message ||
          "Failed to fetch ranks.";
        setRanks([]);
      }

      if (spmResult.status === "fulfilled" && !spmResult.value.error) {
        setSpecimens(spmResult.value.data || []);
        setSpecimensAvailable(true);
      } else {
        setSpecimens([]);
        setSpecimensAvailable(false);
      }

      if (firstError) setFetchError(firstError);
    } catch (error) {
      setFetchError("An unexpected error occurred while fetching data.");
      setScientificNames([]);
      setPublications([]);
      setActTypes([]);
      setRanks([]);
      setSpecimens([]);
      setSpecimensAvailable(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    assignForm(isEdit ? taxonomicAct : null);
    fetchOptions();
  }, [open, isEdit, taxonomicAct, assignForm, fetchOptions]);

  useEffect(() => {
    if (fetchError && !loading) {
      showStatus({
        severity: "error",
        title: "Failed to load required data",
        description: `Failed to load necessary data: ${fetchError}`,
        actions: [
          { label: "Close", onClick: () => onClose?.(false) },
          { label: "Reload Data", variant: "contained", onClick: fetchOptions, closeOnClick: false },
        ],
      });
    }
  }, [fetchError, fetchOptions, loading, onClose, showStatus]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  // ---------- selectors ----------
  const selectedActType = useMemo(
    () => actTypes.find((a) => a.id === form.act_type_id) || null,
    [actTypes, form.act_type_id]
  );
  const selectedScientificName = useMemo(
    () => scientificNames.find((n) => n.id === form.scientific_name_id) || null,
    [scientificNames, form.scientific_name_id]
  );
  const selectedPublication = useMemo(
    () => publications.find((p) => p.id === form.publication_id) || null,
    [publications, form.publication_id]
  );
  const selectedRelatedName = useMemo(
    () => scientificNames.find((n) => n.id === form.related_name_id) || null,
    [scientificNames, form.related_name_id]
  );
  const selectedReplacementName = useMemo(
    () => scientificNames.find((n) => n.id === form.replacement_name_id) || null,
    [scientificNames, form.replacement_name_id]
  );
  const selectedTypeSpecimen = useMemo(
    () => specimens.find((s) => s.id === form.type_specimen_id) || null,
    [specimens, form.type_specimen_id]
  );
  const selectedTypeTaxon = useMemo(
    () => scientificNames.find((n) => n.id === form.type_taxon_id) || null,
    [scientificNames, form.type_taxon_id]
  );

  const isSpeciesLevel = useMemo(() => {
    const rank = selectedScientificName?.current_rank?.toLowerCase() || "";
    return ["species", "subspecies"].includes(rank);
  }, [selectedScientificName]);

  // ---------- view helpers ----------
  const renderLivePreview = () => {
    if (!selectedScientificName || !selectedActType) {
      const snName = selectedScientificName?.name_spell_valid || form.scientific_name_id || "?";
      const actTypeName = selectedActType?.name || form.act_type_id || "?";
      const pubInfo = selectedPublication
        ? normalizePublicationOption(selectedPublication).substring(0, 50) + "..."
        : form.publication_id || "?";
      return (
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          {`Act [${form.id || "New"}]: ${snName} as ${actTypeName} in ${pubInfo}`} <br />
          <i>Select required fields for detailed preview.</i>
        </Typography>
      );
    }
    const target = selectedScientificName?.name_spell_valid || "???";
    const parent = selectedRelatedName?.name_spell_valid || "???";
    const replacement = selectedReplacementName?.name_spell_valid || "???";
    const pubObj = selectedPublication;
    const authors = (pubObj?.publications_authors || [])
      .sort((a, b) => (a?.author_order ?? 0) - (b?.author_order ?? 0))
      .map((pa) => pa.authors?.last_name_eng)
      .filter(Boolean);
    let authorStr = authors.join(", ");
    if (authors.length > 3) authorStr = authors.slice(0, 3).join(", ") + ", et al.";
    const year = pubObj?.publication_date ? new Date(pubObj.publication_date).getFullYear() : "n.d.";
    const publicationStr = `${authorStr} (${year})`;
    const page = form.page || "???";
    const validity = form.validity === "true" ? "valid" : "invalid";
    const newRank = form.rank_change_to || "New Rank";
    switch (selectedActType.code) {
      case "new_taxon":
        return (
          <Typography variant="body2">
            <u><b>{target}</b></u> is described as <u><b>{newRank}</b></u> under <u><b>{parent}</b></u> in <u><b>{publicationStr}</b></u>: page <u><b>{page}</b></u>. This act is regarded as <u><b>{validity}</b></u>.
          </Typography>
        );
      case "syn_nov":
      case "stat_rev":
        return (
          <Typography variant="body2">
            <u><b>{target}</b></u> is treated as a synonym of <u><b>{parent}</b></u> in <u><b>{publicationStr}</b></u>: page <u><b>{page}</b></u>. This act is regarded as <u><b>{validity}</b></u>.
          </Typography>
        );
      case "comb_nov":
        return (
          <Typography variant="body2">
            <u><b>{target}</b></u> is transferred to <u><b>{parent}</b></u> in <u><b>{publicationStr}</b></u>: page <u><b>{page}</b></u>. This act is regarded as <u><b>{validity}</b></u>.
          </Typography>
        );
      case "nomen_nov":
        return (
          <Typography variant="body2">
            <u><b>{target}</b></u> is proposed as a replacement name for <u><b>{replacement}</b></u> in <u><b>{publicationStr}</b></u>: page <u><b>{page}</b></u>. This act is regarded as <u><b>{validity}</b></u>.
          </Typography>
        );
      case "new_status":
        return (
          <Typography variant="body2">
            The status of <u><b>{target}</b></u> is changed to <u><b>{newRank}</b></u> in <u><b>{publicationStr}</b></u>: page <u><b>{page}</b></u>. This act is regarded as <u><b>{validity}</b></u>.
          </Typography>
        );
      default: {
        const actName = selectedActType?.name || "Selected Act";
        return (
          <Typography variant="body2">
            Act '<u><b>{actName}</b></u>' regarding <u><b>{target}</b></u> published in <u><b>{publicationStr}</b></u>: page <u><b>{page}</b></u>. This act is regarded as <u><b>{validity}</b></u>.
          </Typography>
        );
      }
    }
  };

  const renderDynamicFields = () => {
    if (!selectedActType) return null;

    switch (selectedActType.code) {
      case "new_taxon":
      case "new_status":
        return (
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Autocomplete
              options={ranks}
              getOptionLabel={(option) => option || ""}
              value={form.rank_change_to || null}
              onChange={(e, val) => handleChange("rank_change_to", val || "")}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Rank (New Rank / Status)"
                  margin="dense"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start">
                        <LooksOneIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              )}
              PopperComponent={AutocompletePopper}
            />
            <Autocomplete
              options={scientificNames}
              getOptionLabel={(n) => `${n.name_spell_valid} (${n.id})`}
              value={selectedRelatedName}
              onChange={(e, val) => handleChange("related_name_id", val?.id || "")}
              isOptionEqualToValue={(option, value) => option.id === value?.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Parent Taxon"
                  margin="dense"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start">
                        <GroupWorkIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              )}
              PopperComponent={AutocompletePopper}
            />

            {isSpeciesLevel ? (
              specimensAvailable && specimens.length > 0 ? (
                <Box>
                  <Autocomplete
                    options={specimens}
                    getOptionLabel={(s) => `${s.specimen_code} (${s.id})`}
                    value={selectedTypeSpecimen}
                    onChange={(e, val) => handleChange("type_specimen_id", val?.id || "")}
                    isOptionEqualToValue={(option, value) => option.id === value?.id}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Primary Type Specimen"
                        margin="dense"
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <InputAdornment position="start">
                              <BiotechIcon fontSize="small" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    )}
                    PopperComponent={AutocompletePopper}
                  />
                  <Stack direction="row" justifyContent="flex-end" mt={0.5}>
                    <Tooltip title="Add New Specimen">
                      <Button size="small" startIcon={<AddIcon />} onClick={() => setShowAddSpecimen(true)}>
                        Add Specimen
                      </Button>
                    </Tooltip>
                  </Stack>
                </Box>
              ) : (
                <TextField
                  label="Primary Type Specimen ID"
                  value={form.type_specimen_id || ""}
                  onChange={(e) => handleChange("type_specimen_id", e.target.value)}
                  placeholder="Enter Specimen ID"
                  margin="dense"
                  fullWidth
                  helperText={loading ? "Loading specimen info..." : "Specimen list unavailable. Enter ID directly."}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <BiotechIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              )
            ) : (
              <Autocomplete
                options={scientificNames}
                getOptionLabel={(n) => `${n.name_spell_valid} (${n.id})`}
                value={selectedTypeTaxon}
                onChange={(e, val) => handleChange("type_taxon_id", val?.id || "")}
                isOptionEqualToValue={(option, value) => option.id === value?.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Type Taxon"
                    margin="dense"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start">
                          <BiotechIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
                PopperComponent={AutocompletePopper}
              />
            )}
          </Stack>
        );

      case "syn_nov":
      case "stat_rev":
      case "comb_nov":
        return (
          <Autocomplete
            options={scientificNames}
            getOptionLabel={(n) => `${n.name_spell_valid} (${n.id})`}
            value={selectedRelatedName}
            onChange={(e, val) => handleChange("related_name_id", val?.id || "")}
            isOptionEqualToValue={(option, value) => option.id === value?.id}
            renderInput={(params) => (
              <TextField
                {...params}
                label={selectedActType.code === "comb_nov" ? "New Parent Taxon" : "Senior Synonym / Related Taxon"}
                margin="dense"
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <LinkIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            )}
            sx={{ mt: 1 }}
            PopperComponent={AutocompletePopper}
          />
        );

      case "nomen_nov":
        return (
          <Autocomplete
            options={scientificNames}
            getOptionLabel={(n) => `${n.name_spell_valid} (${n.id})`}
            value={selectedReplacementName}
            onChange={(e, val) => handleChange("replacement_name_id", val?.id || "")}
            isOptionEqualToValue={(option, value) => option.id === value?.id}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Name Replaced by this Act"
                margin="dense"
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <FindReplaceIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            )}
            sx={{ mt: 1 }}
            PopperComponent={AutocompletePopper}
          />
        );

      default:
        return null;
    }
  };

  // ---------- save ----------
  const buildPayload = () => ({
    id: form.id,
    scientific_name_id: form.scientific_name_id,
    publication_id: form.publication_id,
    act_type_id: form.act_type_id,
    related_name_id: form.related_name_id || null,
    replacement_name_id: form.replacement_name_id || null,
    rank_change_to: form.rank_change_to || null,
    type_specimen_id: form.type_specimen_id || null,
    type_taxon_id: form.type_taxon_id || null,
    page: form.page || null,
    validity: form.validity === "true",
    remarks: form.remarks || null,
  });

  const handleSave = async () => {
    if (!form.scientific_name_id || !form.publication_id || !form.act_type_id) {
      showStatus({
        severity: "warning",
        title: "Missing required fields",
        description: "Scientific Name, Publication, and Act Type are required before saving.",
      });
      return;
    }
    const payload = buildPayload();
    try {
      if (isEdit) {
        const { error } = await supabase.from("taxonomic_acts").update(payload).eq("id", form.id);
        if (error) throw error;
        setAuditLogProps({
          tableName: "taxonomic_acts",
          rowId: form.id,
          action: "UPDATE",
          beforeData: taxonomicAct,
          afterData: payload,
          onComplete: () => {
            showStatus({
              severity: "success",
              title: "Taxonomic Act updated",
              description: "The record was updated successfully.",
            });
            onClose?.(true);
          },
        });
      } else {
        const { error } = await supabase.from("taxonomic_acts").insert([payload]);
        if (error) throw error;
        setAuditLogProps({
          tableName: "taxonomic_acts",
          rowId: form.id,
          action: "INSERT",
          beforeData: null,
          afterData: payload,
          onComplete: () => {
            showStatus({
              severity: "success",
              title: "Taxonomic Act added",
              description: "The record was created successfully.",
            });
            onClose?.(true);
          },
        });
      }
    } catch (e) {
      showStatus({
        severity: "error",
        title: `${isEdit ? "Update" : "Insert"} failed`,
        description: e.message || String(e),
      });
    }
  };

  // ---------- render ----------
  if (!open) return null;

  return (
    <>
      {loading && <LoadingScreen message="Loading data..." />}


      {!loading && !fetchError && (
        <Dialog open onClose={() => onClose?.(false)} fullWidth maxWidth="md" PaperProps={{ sx: { overflowY: "visible" } }}>
          <DialogTitle>{isEdit ? `Edit Taxonomic Act (ID: ${form.id || "N/A"})` : "Add Taxonomic Act"}</DialogTitle>
          <DialogContent dividers sx={{ overflowY: "auto", position: "relative" }}>
            <Box
              sx={{
                position: "sticky",
                top: -16,
                backgroundColor: "background.paper",
                p: 1.5,
                pt: 1,
                mb: 2,
                zIndex: 10,
                border: 1,
                borderColor: "divider",
                borderRadius: 1,
                boxShadow: 1,
              }}
            >
              <Typography variant="caption" color="textSecondary" gutterBottom>
                Live Preview:
              </Typography>
              {renderLivePreview()}
            </Box>

            <Stack spacing={2.5}>
              <Box>
                <Autocomplete
                  options={scientificNames}
                  getOptionLabel={(n) => `${n.name_spell_valid} (${n.id})`}
                  value={selectedScientificName}
                  onChange={(e, val) => handleChange("scientific_name_id", val?.id || "")}
                  isOptionEqualToValue={(option, value) => option.id === value?.id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      required
                      label="Target Scientific Name"
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <InputAdornment position="start">
                            <ScienceIcon fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                  PopperComponent={AutocompletePopper}
                />
                <Stack direction="row" justifyContent="flex-end" mt={0.5}>
                  <Tooltip title="Add New Scientific Name">
                    <Button size="small" startIcon={<AddIcon />} onClick={() => setShowAddScientificName(true)}>
                      Add Name
                    </Button>
                  </Tooltip>
                </Stack>
              </Box>

              <Box>
                <Autocomplete
                  options={publications}
                  getOptionLabel={normalizePublicationOption}
                  value={selectedPublication}
                  onChange={(e, val) => handleChange("publication_id", val?.id || "")}
                  isOptionEqualToValue={(option, value) => option.id === value?.id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      required
                      label="Publication"
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <InputAdornment position="start">
                            <MenuBookIcon fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                      <Typography
                        variant="body2"
                        sx={{ maxWidth: "calc(100% - 10px)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                      >
                        {normalizePublicationOption(option)}
                      </Typography>
                    </li>
                  )}
                  PopperComponent={AutocompletePopper}
                />
                <Stack direction="row" justifyContent="flex-end" mt={0.5}>
                  <Tooltip title="Add New Publication">
                    <Button size="small" startIcon={<AddIcon />} onClick={() => setShowAddPublication(true)}>
                      Add Publication
                    </Button>
                  </Tooltip>
                </Stack>
              </Box>

              <Autocomplete
                options={actTypes}
                getOptionLabel={(a) => `${a.name} (${a.code})`}
                value={selectedActType}
                onChange={(e, val) => handleChange("act_type_id", val?.id || "")}
                isOptionEqualToValue={(option, value) => option.id === value?.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    required
                    label="Act Type"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start">
                          <CategoryIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
                PopperComponent={AutocompletePopper}
              />

              {renderDynamicFields()}

              <TextField
                label="Page (in publication)"
                value={form.page || ""}
                onChange={(e) => handleChange("page", e.target.value)}
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PageviewIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />

              <Box sx={{ display: "flex", alignItems: "center", pl: 0.5 }}>
                <RuleIcon fontSize="small" sx={{ mr: 1.5, color: "text.secondary" }} />
                <Typography variant="body2" sx={{ mr: 2 }}>
                  Validity:
                </Typography>
                <RadioGroup row value={form.validity} onChange={(e) => handleChange("validity", e.target.value)}>
                  <FormControlLabel value="true" control={<Radio size="small" />} label="Valid" />
                  <FormControlLabel value="false" control={<Radio size="small" />} label="Invalid" />
                </RadioGroup>
              </Box>

              <TextField
                label="Remarks"
                value={form.remarks || ""}
                onChange={(e) => handleChange("remarks", e.target.value)}
                fullWidth
                multiline
                rows={3}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start" sx={{ alignItems: "flex-start", mt: 1 }}>
                      <NotesIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2, borderTop: 1, borderColor: "divider" }}>
            <Button onClick={() => onClose?.(false)} startIcon={<CancelIcon />}>
              Cancel
            </Button>
            <Button onClick={handleSave} variant="contained" color="primary" startIcon={<SaveIcon />}>
              {isEdit ? "Update Act" : "Add Act"}
            </Button>
          </DialogActions>

          {showAddPublication && (
            <DialogPublicationUpsert
              open
              mode="add"
              onClose={(didSave) => {
                setShowAddPublication(false);
                if (didSave) fetchOptions();
              }}
            />
          )}

          {showAddScientificName && (
            <DialogScientificNameEditor
              open
              mode="add"
              onClose={(didSave) => {
                setShowAddScientificName(false);
                if (didSave) fetchOptions();
              }}
            />
          )}
          {showAddSpecimen && (
            <DialogSpecimen
              onClose={() => {
                setShowAddSpecimen(false);
                fetchOptions();
              }}
            />
          )}

          {auditLogProps && <AuditLogUpdater {...auditLogProps} />}
        </Dialog>
      )}
    </>
  );
}


